import { getApps, initializeApp } from "firebase/app";
import { getAnalytics, isSupported, logEvent, setAnalyticsCollectionEnabled } from "firebase/analytics";
import type { Analytics } from "firebase/analytics";
import type { AnalyticsEventName, AnalyticsParams, AnalyticsPrimitive } from "@project/analytics";
import { sanitizeAnalyticsParams } from "@project/analytics";

type FirebaseWebConfig = {
  apiKey: string;
  authDomain?: string;
  projectId: string;
  storageBucket?: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
};

let analyticsPromise: Promise<Analytics | null> | null = null;

function getFirebaseWebConfig(): FirebaseWebConfig | null {
  const apiKey = import.meta.env.VITE_FIREBASE_API_KEY;
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
  const messagingSenderId = import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID;
  const appId = import.meta.env.VITE_FIREBASE_APP_ID;
  if (!apiKey || !projectId || !messagingSenderId || !appId) {
    return null;
  }
  return {
    apiKey,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId,
    appId,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
  };
}

async function getOrInitAnalytics(): Promise<Analytics | null> {
  if (analyticsPromise) {
    return analyticsPromise;
  }
  analyticsPromise = (async () => {
    if (typeof window === "undefined") {
      return null;
    }
    const config = getFirebaseWebConfig();
    if (!config) {
      return null;
    }
    const supported = await isSupported().catch(() => false);
    if (!supported) {
      return null;
    }
    const app = getApps().length > 0 ? getApps()[0]! : initializeApp(config);
    const analytics = getAnalytics(app);
    setAnalyticsCollectionEnabled(analytics, true);
    return analytics;
  })();
  return analyticsPromise;
}

export function initProductAnalytics(): void {
  void getOrInitAnalytics();
}

export async function trackDashboardEvent(
  name: AnalyticsEventName,
  params?: AnalyticsParams,
): Promise<void> {
  const analytics = await getOrInitAnalytics();
  if (!analytics) {
    return;
  }
  const safeParams = sanitizeAnalyticsParams(params);
  const logCustom = logEvent as (
    instance: Analytics,
    eventName: AnalyticsEventName,
    eventParams?: Record<string, AnalyticsPrimitive>,
  ) => void;
  logCustom(analytics, name, safeParams);
}

export function trackDashboardScreenView(pathname: string): void {
  void trackDashboardEvent("screen_view", {
    screen_name: pathname || "/",
    platform: "web",
  });
}

